import type { UseQueryResult } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { OpportunitiesResponse, Opportunity } from "@/api/queries";

import { useGetMyAccount, useGetMyProfile } from "@/api/queries";
import useOpportunitiesQuery from "@/api/sanity/queries/getOpportunities";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import useAppStore from "@/store/appStateZustand";

import type { OpportunitiesApplyFormData } from "../utils/opportunitiesApplySchema";

import { createOpportunitiesApplySchema } from "../utils/opportunitiesApplySchema";
import useWorkSearch from "./useWorkSearch";

type OpportunitiesAvailableData = { opportunities_available?: OpportunitiesResponse };

export function useOpportunitiesApplyForm() {
  const { t } = useTranslation();
  const { opportunityId } = useParams({
    strict: false,
    select: (p): { opportunityId: string | undefined } => ({
      opportunityId: (p as Record<string, string | undefined>).opportunityId,
    }),
  });

  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const allOpportunitiesAvailable = useAppStore(s => s.allOpportunitiesAvailable) as unknown as boolean;

  const { options, cachedSongs, handleInputChange } = useWorkSearch(selectedAccount);

  const myAccount = useGetMyAccount({ artistaccountcode: selectedAccount });
  const userInfo = useGetMyProfile();

  const { data: siteConfig } = useSiteConfigAuth();
  const noOpportunities = siteConfig?.disable_opportunities;

  const oppsQuery = useOpportunitiesQuery() as UseQueryResult<
    OpportunitiesResponse | OpportunitiesAvailableData,
    Error
  >;
  const { isSuccess } = oppsQuery;

  const oppsRaw = oppsQuery.data;
  const oppsData: Opportunity[] = allOpportunitiesAvailable === true
    ? (Array.isArray(oppsRaw) ? oppsRaw : [])
    : (oppsRaw != null && typeof oppsRaw === "object" && "opportunities_available" in oppsRaw
        ? (oppsRaw).opportunities_available ?? []
        : []);

  const [pending, setPending] = useState<boolean>(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState<string>("");

  const form = useForm<OpportunitiesApplyFormData>({
    resolver: zodResolver(createOpportunitiesApplySchema(t)),
    defaultValues: {
      opp: opportunityId ?? "",
      songList: [],
      content: "",
    },
  });

  return {
    form,
    selectedAccount,
    myAccount,
    userInfo,
    oppsData,
    isSuccess,
    noOpportunities,
    pending,
    setPending,
    mutationErrorMessage,
    setMutationErrorMessage,
    options,
    cachedSongs,
    handleInputChange,
  };
}
